import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../../api/client';
import { apiErrorText } from '../../api/errorText';
import type { SettingValue } from '../../api/types';
import { useApi } from '../../api/useApi';
import { useDiscoveryState } from '../../api/useDiscoveryState';
import AsyncSection from '../../components/AsyncSection';
import { languageName } from '../../components/media/languageName';
import ManagedSetting from './ManagedSetting';

/// The languages offered to choose from: the ones films and broadcasts are actually released
/// in, rather than everything Unicode knows a name for. Anything already configured is added to
/// these, so a code set by hand or by an environment variable is never quietly dropped.
const LANGUAGE_CODES = [
  'ar', 'bn', 'cs', 'da', 'de', 'el', 'en', 'es', 'fa', 'fi', 'fr', 'he', 'hi', 'hu', 'id', 'it',
  'ja', 'ko', 'ms', 'nb', 'nl', 'no', 'pl', 'pt', 'ro', 'ru', 'sv', 'ta', 'te', 'th', 'tr', 'uk',
  'ur', 'vi', 'zh',
];

/// The sharpness a floor or a ceiling can be set to. Zero is no limit, and the rest are the
/// pictures things are actually released in.
const HEIGHTS = ['0', '480', '720', '1080', '2160'];

/// The choices for one of the two limits, with whatever is already set among them. A server
/// configured by hand or by an environment variable may hold a height nobody would offer -
/// 1440 - and a select that cannot show it would report the setting as empty.
function heights(current: string, t: (key: string) => string): { value: string; label: string }[] {
  return [...new Set([...HEIGHTS, current])]
    .sort((left, right) => Number(left) - Number(right))
    .map((lines) => ({ value: lines, label: heightLabel(lines, t) }));
}

/// What a version has to be for this server to fetch it: the languages worth watching in, the
/// sharpness worth having them in, and what happens to a version that is neither.
///
/// These belong to the server rather than to whatever fetched the file. An indexer, a television
/// account and whatever is added next all answer the same question - out of forty postings,
/// which one - and answering it in three places is three places to get it wrong.
export default function MediaSearchSettings() {
  const { t, i18n } = useTranslation();
  const settings = useApi(['settings'], api.settings);
  const discovery = useDiscoveryState();

  // A draft, or the value the server reports. No effect keeps the two in step, so they cannot
  // fall out of step either.
  const [draftLanguages, setDraftLanguages] = useState<string[]>();
  const [draftMinimum, setDraftMinimum] = useState<string>();
  const [draftMaximum, setDraftMaximum] = useState<string>();
  const [draftStrict, setDraftStrict] = useState<string>();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const languages = draftLanguages ?? codes(settings.data?.mediaSearchLanguages.value);
  const minimum = draftMinimum ?? height(settings.data?.mediaSearchMinimumHeight.value);
  const maximum = draftMaximum ?? height(settings.data?.mediaSearchMaximumHeight.value);
  const strict = draftStrict ?? (settings.data?.mediaSearchStrict.value ? 'true' : 'false');

  // A floor above the ceiling admits nothing at all, which under strict rules is a server that
  // finds nothing and cannot say why. Caught here rather than written and puzzled over later.
  const impossible = Number(minimum) > 0 && Number(maximum) > 0 && Number(minimum) > Number(maximum);

  const save = async () => {
    setBusy(true);
    setSaved(false);
    setError(undefined);

    try {
      await api.saveSettings({
        mediaSearchLanguages: languages.join(', '),
        mediaSearchMinimumHeight: Number(minimum),
        mediaSearchMaximumHeight: Number(maximum),
        mediaSearchStrict: strict === 'true',
      });

      setDraftLanguages(undefined);
      setDraftMinimum(undefined);
      setDraftMaximum(undefined);
      setDraftStrict(undefined);
      settings.reload();
      setSaved(true);
    } catch (caught) {
      setError(apiErrorText(caught, t).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AsyncSection loading={settings.loading} error={settings.error} onRetry={settings.reload}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={3}>
            {error && <Alert severity="error">{error}</Alert>}
            {saved && <Alert severity="success">{t('settings.saved')}</Alert>}

            <Typography color="text.secondary">{t('settings.mediaSearch.intro')}</Typography>

            {/* Kept either way: rules set before the plugin that uses them are rules, not a
                mistake. What is said is that nothing is deciding by them yet. */}
            {!discovery.canFetch && <Alert severity="info">{t('settings.mediaSearch.noSource')}</Alert>}

            <LanguageOrder
              setting={settings.data?.mediaSearchLanguages}
              languages={languages}
              locale={i18n.language}
              onChange={setDraftLanguages}
            />

            <ManagedSetting
              setting={settings.data?.mediaSearchMinimumHeight}
              label={t('settings.mediaSearch.minimumHeight')}
              hint={t('settings.mediaSearch.minimumHeightHint')}
              value={minimum}
              onChange={setDraftMinimum}
              choices={heights(minimum, t)}
            />

            <ManagedSetting
              setting={settings.data?.mediaSearchMaximumHeight}
              label={t('settings.mediaSearch.maximumHeight')}
              hint={t('settings.mediaSearch.maximumHeightHint')}
              value={maximum}
              onChange={setDraftMaximum}
              choices={heights(maximum, t)}
            />

            {impossible && <Alert severity="error">{t('settings.mediaSearch.impossible')}</Alert>}

            <ManagedSetting
              setting={settings.data?.mediaSearchStrict}
              label={t('settings.mediaSearch.strict')}
              hint={t('settings.mediaSearch.strictHint')}
              value={strict}
              onChange={setDraftStrict}
              choices={[
                { value: 'false', label: t('settings.mediaSearch.strictOff') },
                { value: 'true', label: t('settings.mediaSearch.strictOn') },
              ]}
            />

            <Box>
              <Button variant="contained" onClick={() => void save()} disabled={busy || impossible}>
                {t('common.save')}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </AsyncSection>
  );
}

/// The languages somebody watches in, in the order they want them. The order is the point -
/// it is what a version that names German is ranked above one that names English by - so each
/// one can be moved rather than only added and taken away.
function LanguageOrder({
  setting,
  languages,
  locale,
  onChange,
}: {
  setting: SettingValue<string> | undefined;
  languages: string[];
  locale: string;
  onChange: (languages: string[]) => void;
}) {
  const { t } = useTranslation();
  const managed = setting?.isManagedByEnvironment ?? false;

  // Named in the reader's own language and sorted the way their language sorts, with whatever
  // is already chosen taken out: a list to add from, not a list of everything.
  const options = useMemo(() => {
    const collator = new Intl.Collator(locale);

    return [...new Set([...LANGUAGE_CODES, ...languages])]
      .filter((code) => !languages.includes(code))
      .sort((left, right) => collator.compare(languageName(left, locale), languageName(right, locale)));
  }, [languages, locale]);

  const move = (index: number, by: number) => {
    const moved = [...languages];
    const [code] = moved.splice(index, 1);

    moved.splice(index + by, 0, code);
    onChange(moved);
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        <Typography variant="subtitle2">{t('settings.mediaSearch.languages')}</Typography>
        {managed && (
          <Tooltip title={t('settings.managed')}>
            <LockOutlinedIcon fontSize="small" aria-label={t('settings.managed')} />
          </Tooltip>
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary">
        {t('settings.mediaSearch.languagesHint')}
      </Typography>

      {languages.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('settings.mediaSearch.languagesEmpty')}
        </Typography>
      ) : (
        <Stack>
          {languages.map((code, index) => {
            const name = languageName(code, locale);

            return (
              <Stack key={code} direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ width: 28, flexShrink: 0, textAlign: 'end', fontVariantNumeric: 'tabular-nums' }}
                >
                  {index + 1}
                </Typography>

                <Typography sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                  {name}
                </Typography>

                <IconButton
                  size="small"
                  disabled={managed || index === 0}
                  aria-label={t('settings.mediaSearch.moveUp', { language: name })}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={managed || index === languages.length - 1}
                  aria-label={t('settings.mediaSearch.moveDown', { language: name })}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={managed}
                  aria-label={t('settings.mediaSearch.removeLanguage', { language: name })}
                  onClick={() => onChange(languages.filter((other) => other !== code))}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            );
          })}
        </Stack>
      )}

      {/* Added at the bottom, because that is where it ranks: somebody who wants it first moves
          it there, and nothing they already chose is quietly demoted by the next addition. */}
      <Autocomplete
        options={options}
        disabled={managed}
        value={null}
        blurOnSelect
        getOptionLabel={(code) => languageName(code, locale)}
        onChange={(_, code) => {
          if (code) {
            onChange([...languages, code]);
          }
        }}
        renderInput={(params) => <TextField {...params} label={t('settings.mediaSearch.addLanguage')} />}
        sx={{ maxWidth: 360 }}
      />
    </Stack>
  );
}

/// The two-letter codes out of what the server reports, which is the list as it was written:
/// commas between them, and whatever case somebody typed.
function codes(written: string | undefined): string[] {
  return [
    ...new Set(
      (written ?? '')
        .split(/[,;\s]+/)
        .map((code) => code.split(/[-_]/)[0].toLowerCase())
        .filter((code) => /^[a-z]{2}$/.test(code)),
    ),
  ];
}

/// Zero is what the server calls "no limit", and it is what a select holds for it too.
function height(lines: number | undefined): string {
  return (lines ?? 0).toString();
}

function heightLabel(lines: string, t: (key: string) => string): string {
  return lines === '0' ? t('settings.mediaSearch.heightAny') : `${lines}p`;
}
